import { lazy } from 'react';
import type { ComponentType } from 'react';

const RELOAD_KEY = 'lazy-chunk-reload';

/**
 * Wraps React.lazy() with retry logic for failed dynamic imports.
 *
 * After a deployment, old chunk files (e.g., IssueDetailPage-abc123.js) are replaced
 * with new hashes. Users with a cached index.html still reference the old chunks,
 * causing "Failed to fetch dynamically imported module" errors.
 *
 * This wrapper:
 * 1. Retries the import once after a short delay
 * 2. If it still fails, reloads the page to get the new index.html
 * 3. Uses sessionStorage to prevent infinite reload loops
 */
export function lazyWithRetry<T extends ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const pageHasAlreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === 'true';

    try {
      return await importFn();
    } catch {
      // Retry once after a brief delay (network glitch)
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return await importFn();
      } catch (retryError) {
        // If we haven't already reloaded, do so to get fresh assets
        if (!pageHasAlreadyReloaded) {
          sessionStorage.setItem(RELOAD_KEY, 'true');
          window.location.reload();
          // Return a never-resolving promise to prevent rendering while reloading
          return new Promise(() => {});
        }

        // Already reloaded once — clear the flag and let the error propagate
        // so the ErrorBoundary can show a friendly message
        sessionStorage.removeItem(RELOAD_KEY);
        throw retryError;
      }
    }
  });
}

/**
 * Same as lazyWithRetry but for modules where the component is a named export.
 * Usage: lazyWithRetryNamed(() => import('./MyPage'), 'MyPage')
 */
export function lazyWithRetryNamed<
  M extends Record<string, ComponentType<unknown>>,
  K extends keyof M,
>(importFn: () => Promise<M>, name: K): React.LazyExoticComponent<M[K]> {
  return lazyWithRetry(() => importFn().then((m) => ({ default: m[name] })));
}
